import { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { useQuery } from "@tanstack/react-query";
import Modal from "../modal/Modal";
import SharedTable from "../benefitPlan/SharedTable";
import {
    Alert,
    ModalHeader,
    buildComponentsByName,
    currencyFmt,
    getCoverageTierHeaders,
    summarizeRateWarnings,
} from "./BillingRateBuilderShared";
import { summarizeRateValidation } from "./billingRateBuilderSchemas";
import RateDetailModalContent from "./BillingRateBuilderRateDetail";
import RateEditForm from "./BillingRateBuilderRateEditForm";
import { fetchAccounts } from "../../utils/apiUtils";

const step = 3;

const getRateTitle = (payload) => {
    if (!payload) return "Billing Rate";
    const acct = payload?.account;
    const plan = payload?.benefitPlan;
    if (plan && acct) return `${acct} - ${plan}`;
    return acct || plan || "Billing Rate";
};

const flattenForTable = (payload, index, computedTotals) => {
    const { tierHeaders = [], totals = [] } = computedTotals ?? {};
    const row = {
        Account: payload?.account,
        "Benefit Plan": payload?.benefitPlan,
        __index: index,
    };
    tierHeaders.forEach((tier, i) => {
        row[tier] = currencyFmt.format(totals[i]);
    });
    return row;
};

const getAccountHccIdFromPayload = (payload) =>
    payload?.account ?? payload?.billingRatePayload?.billingRate?.account ?? null;

const BillingRateBuilderStepThree = ({
    activeStep,
    ratePayloads,
    componentPayloads,
    fees,
    uploadContext,
    plans,
    rateCollisions,
    billingCategoryRows,
    effectiveDate,
    onEffectiveDateChange,
    onRateSave,
    onRateAdd,
    onRateDelete,
}) => {
    const [openIndex, setOpenIndex] = useState(null);
    const [mode, setMode] = useState("view");
    const [pendingEditIndex, setPendingEditIndex] = useState(null);
    const [resolvedAccountRow, setResolvedAccountRow] = useState(null);

    const componentsByName = useMemo(
        () => buildComponentsByName(componentPayloads),
        [componentPayloads],
    );

    const validation = useMemo(
        () => summarizeRateValidation(ratePayloads, { prefixRows: true }),
        [ratePayloads],
    );

    const rateWarnings = useMemo(
        () => summarizeRateWarnings(ratePayloads, plans, componentsByName, fees, uploadContext, rateCollisions, { prefixRows: true }),
        [ratePayloads, plans, componentsByName, fees, uploadContext, rateCollisions],
    );

    const rateItems = useMemo(
        () =>
            (ratePayloads ?? []).map((p, i) => ({
                ...flattenForTable(p, i, rateWarnings.totalsByIndex[i]),
                __rowHasError: validation.invalidIndexes.has(i),
                __rowHasWarning: rateWarnings.warningIndexes.has(i),
            })),
        [ratePayloads, rateWarnings, validation],
    );

    const rateColumns = useMemo(() => {
        const tierHeaders = new Set();
        (ratePayloads ?? []).forEach((p) => {
            getCoverageTierHeaders(uploadContext, p?.benefitPlan).forEach((t) =>
                tierHeaders.add(t),
            );
        });
        return ["Account", "Benefit Plan", ...tierHeaders];
    }, [ratePayloads, uploadContext]);

    const pendingPayload =
        pendingEditIndex != null ? ratePayloads?.[pendingEditIndex] : null;
    const pendingAccountHccId = getAccountHccIdFromPayload(pendingPayload);

    const { data: queriedAccountRow, isFetching: isResolvingAccount } = useQuery({
        queryKey: ["billing-rate-edit-account", pendingAccountHccId],
        queryFn: async () => {
            const data = await fetchAccounts({
                accountHccId: pendingAccountHccId,
                limit: 1,
            });
            return data?.rows?.[0] ?? null;
        },
        enabled: Boolean(pendingEditIndex != null && pendingAccountHccId),
        staleTime: Infinity,
    });

    useEffect(() => {
        if (pendingEditIndex == null) return;
        if (!pendingAccountHccId) {
            setResolvedAccountRow(null);
            setMode("edit");
            setPendingEditIndex(null);
            return;
        }
        if (queriedAccountRow !== undefined) {
            setResolvedAccountRow(queriedAccountRow);
            setMode("edit");
            setPendingEditIndex(null);
        }
    }, [pendingEditIndex, pendingAccountHccId, queriedAccountRow]);

    const closeModal = () => {
        setOpenIndex(null);
        setMode("view");
        setPendingEditIndex(null);
        setResolvedAccountRow(null);
    };

    const handleDetailClick = (row) => {
        setOpenIndex(row.__index);
        setMode("view");
        return row;
    };

    const handleEditClick = () => {
        if (openIndex == null) return;
        setPendingEditIndex(openIndex);
    };

    const handleAddClick = () => {
        setOpenIndex(null);
        setMode("add");
    };

    const handleDelete = (row) => {
        const index = row?.__index;
        if (index == null) return;
        onRateDelete?.(index);
    };

    const openPayload = openIndex != null ? ratePayloads?.[openIndex] : null;
    const title = getRateTitle(openPayload);
    const isEditLoading = pendingEditIndex != null && isResolvingAccount;

    if (activeStep !== step) return null;

    return (
        <div className="billing-rate-builder__step container">
            <div className="billing-rate-builder__effective-date">
                <label className="billing-rate-builder__effective-date-label">Effective Date</label>
                <DatePicker
                    className="date-picker"
                    selected={effectiveDate}
                    onChange={onEffectiveDateChange}
                    dateFormat="yyyy-MM-dd"
                    isClearable={true}
                    placeholderText="Select effective date"
                />
                {effectiveDate && (
                    <span className="billing-rate-builder__effective-date-note">
                        Applies to new and updated rate lock dates
                    </span>
                )}
            </div>
            {ratePayloads?.length === 0 ? (
                <Alert
                    variant="warning"
                    title="No rates exist."
                    footer="Rates must be created in order to submit to HRP."
                />
            ) : (
                <>
                    {validation.hasErrors && (
                        <Alert
                            variant="error"
                            title={`Errors detected in ${validation.invalidCount} rate payload${
                                validation.invalidCount === 1 ? "" : "s"
                            }.`}
                            body={validation.bodyLines}
                            footer={'Please click on the rate\'s "Detail" button to correct these errors.'}
                        />
                    )}
                    {rateWarnings.hasWarnings && (
                        <Alert
                            variant="warning"
                            title={`Warnings detected in ${rateWarnings.warningCount} rate payload${
                                rateWarnings.warningCount === 1 ? "" : "s"
                            }.`}
                            body={rateWarnings.warningLines}
                            footer="It's recommended to investigate these warnings before submitting to HRP."
                        />
                    )}
                    {!validation.hasErrors && !rateWarnings.hasWarnings && (
                        <Alert
                            variant="success"
                            title="All rate payloads passed validation."
                            footer="No validation errors were found. Rate payloads are ready for submission to HRP."
                        />
                    )}
                </>
            )}
            <div className="billing-rate-builder__page-header">
                <p className="title">
                    {rateItems.length > 0 ? `Rates (${rateItems.length})` : "Rates"}
                </p>
                <button
                    type="button"
                    className="btn edit-btn"
                    onClick={handleAddClick}
                >
                    + Add
                </button>
            </div>
            <SharedTable
                type="billing-rates"
                columns={rateColumns}
                items={rateItems}
                isLoading={false}
                isFetching={false}
                hasNextPage={false}
                disableModel={true}
                disableHeaderFormatting={true}
                onDetailHandler={handleDetailClick}
                onDeleteHandler={handleDelete}
                noResultsMessage="No billing rates."
            />

            <Modal
                isOpen={openPayload != null || mode === "add"}
                onClose={closeModal}
            >
                {openPayload && mode === "view" && (
                    <div className="billing-rate-builder__modal-content scrollable-form">
                        <ModalHeader
                            title={title}
                            onEdit={handleEditClick}
                            isEditLoading={isEditLoading}
                        />
                        <RateDetailModalContent
                            payload={openPayload}
                            componentsByName={componentsByName}
                            billingCategoryRows={billingCategoryRows}
                            fees={fees}
                            uploadContext={uploadContext}
                            plans={plans}
                        />
                    </div>
                )}
                {openPayload && mode === "edit" && (
                    <div className="billing-rate-builder__modal-content scrollable-form">
                        <RateEditForm
                            initial={openPayload}
                            resolvedAccountRow={resolvedAccountRow}
                            componentsByName={componentsByName}
                            billingCategoryRows={billingCategoryRows}
                            onBack={() => setMode("view")}
                            onSave={(newPayload) => {
                                onRateSave?.(openIndex, newPayload);
                                setMode("view");
                            }}
                        />
                    </div>
                )}
                {mode === "add" && (
                    <div className="billing-rate-builder__modal-content">
                        <RateEditForm
                            initial={null}
                            resolvedAccountRow={null}
                            componentsByName={componentsByName}
                            billingCategoryRows={billingCategoryRows}
                            defaultLockDate={effectiveDate}
                            onBack={closeModal}
                            onSave={(newPayload) => {
                                onRateAdd?.(newPayload);
                                closeModal();
                            }}
                        />
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default BillingRateBuilderStepThree;
